<template>
  <WidgetBaseComponent class="expedient-summary-widget" :loadedData="allDataLoaded && !showSpinner">
    <!-- HEADER -->
    <template #header>
      <h2>{{ expedientSummaryData.title }}</h2>
    </template>

    <!-- BODY -->
    <template #body>
      <section class="expedient-summary-widget-container">
        <div class="expedient-info" v-resize="checkTruncation">
          <div v-for="(item, index) in infoItems" :key="index" class="info-item">
            <PhoneListTooltip
              v-if="isTruncatedItems[index]"
              :item="item"
              :index="index"
              :isTruncated="isTruncatedItems[index]"
            />
            <div v-else class="row-info">
              <template v-if="Array.isArray(item.data)">
                <PhoneListComponent :label="item.label" :data="item.data" :index="index" @setRef="setRef" />
              </template>
              <template v-else>
                <span class="info-item-label">{{ `${item.label}:` }}</span>
                <div class="info-item-data-large">{{ item.data }}</div>
              </template>
            </div>
          </div>
        </div>

        <div class="resumes-container">
          <div v-for="(counter, index) in parseResumeCounters" :key="`counter-${index}`" class="resume-item">
            <span :class="[renderIcon(index), 'lf-icon__md']"></span>
            <div class="resume-item-container">
              <span class="resume-item-title">{{ counter.title }}</span>
              <span class="resume-item-qty">{{ counter.qty }}</span>
            </div>
            <div class="tooltip-container">
              <LexonTooltipComponent :icon="icon.tooltip" :message="counter.message" position="right" />
            </div>
          </div>
        </div>
      </section>
    </template>
  </WidgetBaseComponent>
</template>

<script setup lang="ts">
import { ref, computed, watch, onBeforeUnmount, onMounted } from 'vue'
import store from '@/store/store'
import WidgetBaseComponent from '@/components/widgets/WidgetBaseComponent.vue'
import { Icons } from '@/icons/icons'
import { ContextName, ModuleNamespaces } from '@/store/types/storeGlobalTypes'
import LexonTooltipComponent from '@/components/forms/fields/LexonTooltip/LexonTooltipComponent.vue'
import PhoneListComponent from '@/components/widgets/ExpedientWidgets/ExpedientSummaryWidget/PhoneListComponent.vue'
import PhoneListTooltip from '@/components/widgets/ExpedientWidgets/ExpedientSummaryWidget/PhoneListTooltip.vue'
import useSelectedRegister from '@/composables/useSelectedRegister'
import useStage from '@/composables/useStage'
import useEntity from '@/composables/useEntity'
import useEndpoints from '@/composables/useEndpoints'
import useTruncation from '@/composables/useTruncation'

// CONSTANTS
const selectedRegisterModule = ModuleNamespaces.SELECTED_REGISTER

const icon = {
  bill: Icons.BILL,
  clock: Icons.CLOCK,
  money: Icons.BILLING,
  tooltip: Icons.INFO_FULL
}

const contexts = {
  expedientSummary: ContextName.EXPEDIENT_SUMMARY_WIDGET,
  expedientContext: ContextName.EXPEDIENTS
}

// DATA
const showSpinner = ref(true)

// COMPUTED
const expedientStageStateGetter = computed(() => expedientStage.value)

const expedientSummaryData = computed(() => {
  return selectedRegisterData.value?.globalVisionExpSummary || { body: [], counters: [] }
})

const infoItems = computed(() => {
  if (!expedientSummaryData.value) return []
  return expedientSummaryData.value.body
})

const allDataLoaded = computed(() => {
  if (!expedientSummaryData.value) return false
  return !!(expedientSummaryData.value && Object.keys(expedientSummaryData.value).length)
})

const parseResumeCounters = computed(() => {
  if (!expedientSummaryData.value) return []
  return expedientSummaryData.value.counters.map((item: any) => ({
    title: item.label,
    qty: item.data,
    message: item.tooltip || ''
  }))
})

// COMPOSABLES
const expedientRegister = useSelectedRegister({ entityName: contexts.expedientContext })
const summaryInfo = useSelectedRegister({ entityName: contexts.expedientSummary })

const { selectedRegisterId: selectedExpedientId, loadSelectedRegisterWithPost } = expedientRegister
const { selectedRegisterData } = summaryInfo

const { expedientStage } = useStage()
const { getEntity } = useEntity({ entityName: contexts.expedientContext })
const { getEndpoints } = useEndpoints()

const { isTruncatedItems, checkTruncation, setRef } = useTruncation({
  items: infoItems,
  debounceTime: 0
})

// METHODS
const fetchSummaryInfo = async () => {
  showSpinner.value = true

  const entity = getEntity()
  const entityType = entity.type
  const endpoints = getEndpoints(entityType)
  const endpoint = endpoints.get

  await loadSelectedRegisterWithPost({
    endpoint,
    context: contexts.expedientSummary,
    post: {
      idExpedient: selectedExpedientId.value,
      source: contexts.expedientSummary,
      idStage: expedientStageStateGetter.value
    }
  })

  showSpinner.value = false
}

const renderIcon = (index: number) => {
  switch (index) {
    case 0:
      return icon.clock
    case 1:
      return icon.bill
    default:
      return icon.money
  }
}

// WATCHERS
watch(expedientStageStateGetter, () => {
  fetchSummaryInfo()
})

// HOOKS
onMounted(async () => {
  await fetchSummaryInfo()
})

onBeforeUnmount(() => {
  store.commit(`${selectedRegisterModule}/REMOVE_SELECTED_REGISTER_DATA_CONTEXT`, contexts.expedientSummary)
})
</script>

<style lang="scss" scoped>
.expedient-summary-widget {
  border-radius: $cornerRadius-md;
  border: 1px solid $main-300;
  min-height: 146px;

  .expedient-summary-widget-container {
    display: flex;
    align-items: center;
    flex-direction: column;
    gap: $spacing-xs;
    width: 100%;

    .expedient-info {
      display: flex;
      padding: $spacing-sm;
      flex-direction: column;
      align-items: flex-start;
      width: 100%;
      gap: 8px;
      flex: 1;
      background: $neutral-grey-050;

      .info-item {
        display: flex;
        flex-direction: column;
        width: 100%;
        margin-bottom: $spacing-xs;
        height: 28px;

        .row-info {
          display: flex;
          width: 100%;
          align-items: center;
          padding: $spacing-xxs 0;
          background-color: transparent;
        }

        .info-item-label {
          @include milano-bold-16;
          color: $neutral-grey-800;
          padding: 0 $spacing-xxs;
          margin-right: $spacing-xs;
          white-space: nowrap;
          flex-shrink: 0;
        }

        .info-item-data-large {
          @include milano-regular-14;
          color: $neutral-grey-800;
          flex: 1;
          min-width: 0;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          max-width: none;
          width: 100%;
        }
      }
    }

    .resumes-container {
      display: flex;
      width: 100%;
      height: 100%;
      align-items: flex-start;
      gap: 8px $spacing-xs;
      flex-wrap: wrap;

      .resume-item {
        display: flex;
        align-items: flex-start;
        width: 100%;
        height: 47%;
        gap: $spacing-xs;
        padding: $spacing-xs $spacing-sm;
        border-radius: $cornerRadius-sm;
        border: 1px solid $main-200;

        .resume-item-container {
          display: flex;
          flex-direction: column;

          .resume-item-title {
            @include milano-regular-14;
            color: $neutral-grey-800;
            margin-bottom: 4px;
          }

          .resume-item-qty {
            @include milano-bold-16;
            color: $main-1000;
          }
        }
      }

      .tooltip-container {
        --tooltip-icon-margin-left: 6px;
        --tooltip-icon-size: 13px;
        --tooltip-icon-color: $neutral-grey-600;
        padding-top: 3px;
      }
    }
  }
}

@media (min-width: 600px) {
  .expedient-summary-widget {
    .expedient-summary-widget-container {
      .resumes-container {
        width: 100%;
        height: 146px;

        .resume-item {
          width: 49%;
        }
      }
    }
  }
}

@media (min-width: 1025px) {
  .expedient-summary-widget {
    .expedient-summary-widget-container {
      .expedient-info {
        display: flex;
        flex-flow: row wrap;
        justify-content: space-between;

        .info-item {
          width: calc(33.33% - 8px);
          box-sizing: border-box;
          padding-right: $spacing-xs;
        }
      }

      .resumes-container {
        width: 100%;
        height: auto;

        .resume-item {
          width: calc(25% - 7px);
        }
      }
    }
  }
}

@include desktopSuperLarge {
  .expedient-summary-widget {
    .expedient-summary-widget-container {
      flex-direction: column;

      .expedient-info {
        width: 100%;
      }
    }
  }
}
</style>
